import React from "react";
import * as Icons from "lucide-react";
import { Target, Eye } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import PageHero from "@/components/common/PageHero";
import { Reveal, RevealGroup, RevealItem } from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import Counter from "@/components/common/Counter";
import CTASection from "@/components/sections/CTASection";
import { STRENGTHS, VALUES } from "@/data/site";

export default function About() {
  useSEO({
    title: "Nosotros — Quiénes somos",
    description:
      "GEMA es una empresa colombiana especializada en mantenimiento automotriz, industrial y naval, comprometida con la calidad, el cumplimiento y la mejora continua.",
    path: "/nosotros",
  });

  return (
    <>
      <PageHero
        overline="Sobre Nosotros"
        title="Aliados estratégicos en mantenimiento y suministro técnico"
        subtitle="Combinamos experiencia multisectorial, personal calificado y procesos organizados para ofrecer soluciones confiables a clientes particulares, empresas privadas y entidades públicas."
        image="https://images.unsplash.com/photo-1738918917045-ac8cf1289a48"
        crumbs={[{ label: "Nosotros" }]}
      />

      {/* History */}
      <section className="bg-gema-black py-20 md:py-28">
        <div className="container-gema grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative overflow-hidden">
              <img src="https://images.unsplash.com/photo-1569875875269-6943dc3154c4" alt="Mantenimiento naval" className="w-full h-[480px] object-cover grayscale hover:grayscale-0 transition-all duration-700" loading="lazy" />
              <div className="absolute inset-0 border border-white/10 pointer-events-none" />
            </div>
          </Reveal>
          <div>
            <Reveal><SectionLabel>Nuestra Historia</SectionLabel></Reveal>
            <Reveal delay={0.1}><h2 className="heading-lg text-white mt-6">Capacidad técnica al servicio de la industria</h2></Reveal>
            <Reveal delay={0.2}>
              <p className="text-white/60 mt-6 leading-relaxed">
                GESTIÓN ESPECIALIZADA MANTENIMIENTOS & SUMINISTROS S.A.S. (GEMA) nace con el propósito de brindar
                servicios integrales de mantenimiento naval, industrial y automotriz, así como el suministro
                especializado de repuestos y componentes técnicos.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="text-white/60 mt-4 leading-relaxed">
                Trabajamos con personal calificado y procedimientos organizados para ofrecer soluciones confiables
                que aseguren el óptimo funcionamiento de maquinaria, motores y sistemas eléctricos, contribuyendo a
                la continuidad operativa de entidades públicas y privadas.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-gema-ink py-20 md:py-28">
        <div className="container-gema grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal>
            <div className="border border-white/10 p-10 h-full">
              <Target className="h-10 w-10 text-gema-red" strokeWidth={1.5} />
              <h3 className="font-heading font-black uppercase text-2xl text-white mt-6">Misión</h3>
              <p className="text-white/60 mt-4 leading-relaxed">
                Brindar servicios integrales de mantenimiento naval, industrial y automotriz, así como el suministro
                especializado de repuestos y componentes técnicos, garantizando altos estándares de calidad, eficiencia
                y cumplimiento en cada proceso ejecutado.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-white/10 p-10 h-full">
              <Eye className="h-10 w-10 text-gema-red" strokeWidth={1.5} />
              <h3 className="font-heading font-black uppercase text-2xl text-white mt-6">Visión</h3>
              <p className="text-white/60 mt-4 leading-relaxed">
                Consolidarnos como una empresa reconocida por su capacidad técnica y responsabilidad en la prestación de
                servicios de mantenimiento naval, industrial y automotriz, destacándonos por el cumplimiento contractual,
                la confiabilidad y la excelencia en la ejecución.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-20 md:py-28">
        <div className="container-gema">
          <Reveal><SectionLabel dark={false}>Valores</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="heading-lg text-black mt-6">Los principios que guían nuestro trabajo</h2></Reveal>
          <RevealGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10 mt-12">
            {VALUES.map((v) => {
              const Icon = Icons[v.icon] || Icons.Circle;
              return (
                <RevealItem key={v.title}>
                  <div className="bg-white p-8 h-full">
                    <Icon className="h-8 w-8 text-gema-red" strokeWidth={1.5} />
                    <h3 className="font-heading font-bold uppercase text-black mt-6">{v.title}</h3>
                    <p className="text-black/50 text-sm mt-3 leading-relaxed">{v.text}</p>
                  </div>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* Strengths counters */}
      <section className="bg-gema-black py-20 md:py-28">
        <div className="container-gema">
          <RevealGroup className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STRENGTHS.map((s) => (
              <RevealItem key={s.title}>
                <div className="text-center">
                  <Counter to={s.stat} suffix={s.statSuffix} className="font-heading font-black text-5xl text-white" />
                  <p className="text-white/40 text-xs font-mono uppercase tracking-wider mt-3">{s.title}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <CTASection />
    </>
  );
}
